import { useRouter } from 'next/router';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Select, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import type { Seo } from '@sravni/types/lib/seo';

import type * as Microcredits from '@src/@types/microcredits';
import { AuthSwitch } from '@src/components/AuthSwitch';
import { AmountControlV2 } from '@src/components/ui/AmountControlV2';
import { PRODUCT_LIST_ANCHOR } from '@src/constants/common';
import { FILTERS_NAMES, SORT_VALUES } from '@src/constants/filters';
import {
    sendChangeObtainingMethodFilterEvent,
    sendChangeTermFilterEvent,
    sendUserWantFreeLoanEvent,
} from '@src/helpers/analyticsEvents';
import { scrollToElementById } from '@src/helpers/common';
import { isNaKartuPage } from '@src/helpers/isNaKartuPage';
import { openAuthDialog } from '@src/reducers/authDialog';
import { useUser } from '@src/reducers/user';

import styles from './styles.module.scss';

const { Heading } = Typography;

interface IPageHeaderFiltersProps {
    className?: string;
    difference?: number;
    filters: Microcredits.IFilters;
    conditionsActive?: boolean;
    onChange: (value: any, name: string) => void;
    onToggleConditionDialog?: () => void;
    seo?: Partial<Seo>;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const label = 'Мне нужно';
const MOBILE_BUTTON_SIZE = 52;
const DESKTOP_BUTTON_SIZE = 60;

enum LoanTerm {
    ANY = 'any',
    LESS_THAN_MONTH = 'less_month',
    LESS_THAN_TWO_MONTHS = 'less_two_months',
    MORE_THAN_TWO_MONTHS = 'more_than_two_months',
}

const termOptions = [
    { label: 'Любой', value: LoanTerm.ANY },
    { label: 'До месяца', value: LoanTerm.LESS_THAN_MONTH },
    { label: 'До 2х месяцев', value: LoanTerm.LESS_THAN_TWO_MONTHS },
    { label: 'От 2х месяцев', value: LoanTerm.MORE_THAN_TWO_MONTHS },
];

const obtainingMethodOptions = [
    { label: 'Любой', value: '' },
    { label: 'Карта или счёт', value: 'card-or-account' },
    { label: 'Электронный кошелек', value: 'any-e-wallet' },
    { label: 'Наличные', value: 'cash' },
];

const termFiltersValues: Record<LoanTerm, Partial<Record<FILTERS_NAMES, any>>> = {
    [LoanTerm.ANY]: {
        [FILTERS_NAMES.TERM_RANGE_FROM]: '',
        [FILTERS_NAMES.TERM_RANGE_TO]: '',
        [FILTERS_NAMES.SORT]: '',
    },
    [LoanTerm.LESS_THAN_MONTH]: {
        [FILTERS_NAMES.TERM_RANGE_FROM]: 0,
        [FILTERS_NAMES.TERM_RANGE_TO]: 30,
        [FILTERS_NAMES.SORT]: SORT_VALUES.TERM_ASC,
    },
    [LoanTerm.LESS_THAN_TWO_MONTHS]: {
        [FILTERS_NAMES.TERM_RANGE_FROM]: 0,
        [FILTERS_NAMES.TERM_RANGE_TO]: 60,
        [FILTERS_NAMES.SORT]: SORT_VALUES.TERM_ASC,
    },
    [LoanTerm.MORE_THAN_TWO_MONTHS]: {
        [FILTERS_NAMES.TERM_RANGE_FROM]: 61,
        [FILTERS_NAMES.TERM_RANGE_TO]: 10 ** 5,
        [FILTERS_NAMES.SORT]: SORT_VALUES.TERM_DESC,
    },
};

export const PageHeaderFilters: React.FunctionComponent<IPageHeaderFiltersProps> = React.memo(
    ({ filters, onChange }) => {
        const isMobile = useIsMobile();
        const user = useUser();
        const { asPath } = useRouter();
        const dispatch = useDispatch();

        const [pathWithoutQuery] = asPath.split('?');
        const showAuthSwitch = !user && isNaKartuPage(pathWithoutQuery);

        const handleClickSelectionButton = useCallback(() => {
            scrollToElementById(PRODUCT_LIST_ANCHOR);
        }, []);

        const handleChangeObtainingMethodFilter = useCallback(
            (value) => {
                sendChangeObtainingMethodFilterEvent(
                    obtainingMethodOptions.find((el) => el.value === value)?.label ?? '',
                );

                return onChange(value, FILTERS_NAMES.OBTAINING_METHOD);
            },
            [onChange],
        );

        const handleTermChange = useCallback(
            (value: any) => {
                if (!value) return;
                const optionFilters = termFiltersValues[value as LoanTerm];

                sendChangeTermFilterEvent(termOptions.find((el) => el.value === value)?.label ?? '');

                Object.entries(optionFilters).forEach(([key, filterValue]) => onChange(filterValue, key));
            },
            [onChange],
        );

        const onSwitchClick = useCallback(() => {
            sendUserWantFreeLoanEvent();
            dispatch(openAuthDialog({ dialogInitialType: 'firstFreeLoan' }));
        }, [dispatch]);

        return (
            <div className={styles.wrapper}>
                {isMobile && (
                    <Heading level={1} className="h-text-center h-mb-8">
                        Займы онлайн
                    </Heading>
                )}
                <div className={styles.filters}>
                    <div className={styles.sumField}>
                        <AmountControlV2
                            name={FILTERS_NAMES.AMOUNT}
                            className={styles.amount}
                            label={label}
                            value={filters[FILTERS_NAMES.AMOUNT]}
                            max={100000}
                            onChange={onChange}
                            postfix="₽"
                            steps={[
                                { ceilThreshold: 30000, step: 1000 },
                                { ceilThreshold: 100000, step: 5000 },
                            ]}
                        />
                    </div>
                    <div className={styles.sumField}>
                        <Select
                            label="На срок"
                            value={getTermLabel(termOptions, filters[FILTERS_NAMES.TERM_RANGE_TO] || 0)}
                            options={termOptions}
                            onChange={handleTermChange}
                            optionsSort={false}
                        />
                    </div>
                    <div className={styles.sumField}>
                        <Select
                            label="Способ получения"
                            value={filters[FILTERS_NAMES.OBTAINING_METHOD] ?? obtainingMethodOptions[0].value}
                            options={obtainingMethodOptions}
                            onChange={handleChangeObtainingMethodFilter}
                            optionsSort={false}
                        />
                    </div>
                    {showAuthSwitch && isMobile && <AuthSwitch onClick={onSwitchClick} />}

                    <Button
                        variant="primary"
                        size={isMobile ? MOBILE_BUTTON_SIZE : DESKTOP_BUTTON_SIZE}
                        onClick={handleClickSelectionButton}
                        className={styles.button}
                    >
                        Показать предложения
                    </Button>
                </div>
                {showAuthSwitch && !isMobile && <AuthSwitch onClick={onSwitchClick} />}
            </div>
        );
    },
);

function getTermLabel(options: Array<{ label: string; value: LoanTerm }>, filterValue: number) {
    const appropriateOption = options.find((option) => filterValue <= termFiltersValues[option.value].termRangeTo);

    return (appropriateOption ?? options[options.length - 1]).value;
}
